import React, { useState } from "react";
import { connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { Button, Card, Container, Form } from "react-bootstrap";
import BootStrapSpinner from "react-bootstrap/Spinner";

import {
  emptyString,
  loginConstants,
  paramsConstants,
  textStrings,
  viewPathConstants,
  toastTypes,
  PASSWORD_MIN_LENGTH,
  PASSWORD_VALIDATION_PATTERN,
  passwordValidationMessages,
  DEFAULT_PASSWORD_CHECK_VALIDATION,
  STYLES,
} from "../../constants";
import { loginActions } from "../../redux/actions";
import {
  useSpinner,
  customToast,
  getPasswordValidationState,
  PasswordValidationChecklist,
  handleAsyncAction,
} from "../../shared";
import logo from "../../assets/images/hunger-rush-logo.webp";
import { generateImagePath } from "../../shared/components/sidebarContainer/utility";

const ConfirmPassword = (props) => {
  const {
    imagePathFromHub,
    resetForgotEmailResponse,
    resetPasswordByEncryptedParams,
  } = props;

  //Initital states
  const [data, setData] = useState(emptyString);
  const [checkValidation, setCheckValidation] = useState(
    DEFAULT_PASSWORD_CHECK_VALIDATION,
  );

  const [encryptedParameters] = useSearchParams();
  const token = encryptedParameters.get(paramsConstants.VERIFICATION_TOKEN);

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  //Handle change in password and confirm password
  const handleChange = (e) => {
    const { name, value } = e.target;

    //If user change password check validation for highlghting labels
    setData((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === loginConstants.TYPE_PASSWORD) {
        setCheckValidation(
          getPasswordValidationState(value, updated.confirmPassword),
        );
      } else {
        setCheckValidation((prevValidation) => ({
          ...prevValidation,
          checkEquality: value ? value === updated.password : false,
        }));
      }

      return updated;
    });
  };

  // Show loading while a request is running.
  const spinner = useSpinner();

  //Function to submit form
  const submit = () => {
    //Check if password and confirm password are equal esle show toast
    if (checkValidation.checkEquality) {
      const body = {
        password: data?.password,
        confirmPassword: data?.confirmPassword,
        encryptedParameters: token,
      };
      //If backend response is success then navigate to login
      handleAsyncAction(resetPasswordByEncryptedParams(body), {
        onSuccess: () => {
          navigate(viewPathConstants.LOGIN);
          resetForgotEmailResponse();
        },
      });
    } else {
      customToast(loginConstants.PASSWORD_NOT_EQUAL, toastTypes.ERROR);
    }
  };

  return (
    <Container className="d-flex align-items-center justify-content-center">
      <Card className="border-0 vh-100">
        <Card.Body className="d-flex align-items-center">
          <Container className="mx-auto shadow-lg rounded px-5 pb-5 pt-2 ">
            <div className="text-center pb-3">
              <img
                src={generateImagePath(imagePathFromHub, logo)}
                alt="logo"
                width={60}
                className="mb-2"
              />
              <span className="login-platform-name login-platform-firstName-color">
                Hunger
              </span>
              <span className="login-platform-name login-platform-secondName-color">
                Rush
              </span>
            </div>
            <Form onSubmit={handleSubmit(submit)} className="w-100">
              <Form.Group controlId="password">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  min={PASSWORD_MIN_LENGTH}
                  type="password"
                  name="password"
                  aria-label="password"
                  className={`form-control ${
                    errors.password ? STYLES.IS_INVALID : emptyString
                  }`}
                  {...register("password", {
                    required: true,
                    pattern: {
                      value: PASSWORD_VALIDATION_PATTERN,
                      message: passwordValidationMessages.NOT_VALID,
                    },
                    onChange: (e) => handleChange(e),
                  })}
                />
              </Form.Group>
              <Form.Group controlId="confirmPassword" className="mt-2">
                <Form.Label>Confirm Password</Form.Label>
                <Form.Control
                  min={PASSWORD_MIN_LENGTH}
                  type="password"
                  name="confirmPassword"
                  aria-label="confirm-password"
                  className={`form-control ${
                    errors.confirmPassword ? STYLES.IS_INVALID : emptyString
                  }`}
                  {...register("confirmPassword", {
                    required: true,
                    pattern: {
                      value: PASSWORD_VALIDATION_PATTERN,
                      message: passwordValidationMessages.NOT_VALID,
                    },
                    onChange: (e) => handleChange(e),
                  })}
                />
                <Form.Text className="text-muted">
                  <PasswordValidationChecklist
                    checkValidation={checkValidation}
                  />
                </Form.Text>
              </Form.Group>
              <div className="text-end mt-4">
                <Button
                  type="submit"
                  disabled={spinner(loginConstants.RESET_PASSWORD_SPINNER)}
                >
                  {textStrings.RESET_PASSWORD}
                  <span>
                    {spinner(loginConstants.RESET_PASSWORD_SPINNER) ? (
                      <BootStrapSpinner
                        animation="border"
                        size="sm"
                        className="ms-1"
                      />
                    ) : (
                      <></>
                    )}
                  </span>
                </Button>
              </div>
            </Form>
          </Container>
        </Card.Body>
      </Card>
    </Container>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  imagePathFromHub: state.system.fromHub?.imagePath,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  resetPasswordByEncryptedParams: (body) =>
    dispatch(loginActions.resetPasswordByEncryptedParams(body)),
  resetForgotEmailResponse: () =>
    dispatch(loginActions.resetForgotEmailResponse()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ConfirmPassword);
